import React, { useState, useEffect, useRef } from 'react'

import { Mercator } from '@visx/geo'
import { Zoom } from '@visx/zoom'
import { localPoint } from '@visx/event'
import { LegendThreshold } from '@visx/legend'
import { scaleThreshold } from '@visx/scale'
import { ParentSize } from '@visx/responsive'
import { Annotation, Connector, Label, CircleSubject } from '@visx/annotation'

import * as topojson from 'topojson-client'
import { GeometryObject, Topology } from 'topojson-specification'

import styled from 'styled-components'
import { browserCheck } from '@ta-interaktiv/browsercheck'

import Panel from 'components/Panel'
import genevaContour from './geneve_lac.json'
import cityPoints from './cities.json'
import { annotations as staticAnnotations } from './annotations'

const { features: GenevaContour } = topojson.feature(
  genevaContour as unknown as Topology,
  genevaContour.objects.cantons as GeometryObject
) as unknown as {
  type: 'FeatureCollection'
  features: FeaturePolygon[]
}

const { features: cities } = topojson.feature(
  cityPoints as unknown as Topology,
  cityPoints.objects.cities as GeometryObject
) as unknown as {
  type: 'FeatureCollection'
  features: FeaturePoint[]
}

const height = browserCheck.isMobile ? 400 : 500

const Map = ({
  department,
  dataVersion,
  propertyType,
  roomCount,
  searchInput,
  locked,
  useStaticThresholds,
  getMunicipalityNames,
}: {
  department: 'ain' | 'haute-savoie'
  dataVersion: Dictionary
  propertyType: 'appartement' | 'maison'
  roomCount: '' | '1' | '2' | '3' | '4' | '5' | '6'
  searchInput: string | null
  locked: boolean
  useStaticThresholds: boolean
  getMunicipalityNames: (municipalityNames: string[]) => void
}) => {
  const defaultScale = browserCheck.isMobile
    ? department === 'ain'
      ? 11000
      : 10000
    : 15000
  const zoomedScale = browserCheck.isMobile ? 20000 : 25000
  const [scale, setScale] = useState(defaultScale)
  const [shiftY, setShiftY] = useState(0)
  const panelRef = useRef<HTMLDivElement>(null)

  const mainCentroid: [number, number] =
    department === 'ain' ? [5.6, 46.2] : [6.42831, 46.25]

  const [polygons, setPolygons] = useState<FeaturePolygon[]>([])
  const [municipalityNames, setMunicipalityNames] = useState<string[]>([])
  const [immoData, setImmoData] = useState<ImmoDataRow[]>([])
  const [showPanel, setShowPanel] = useState(false)
  const [hoveredFeature, setHoveredFeature] = useState<FeaturePolygon | null>(
    null
  )
  const [selectedFeature, setSelectedFeature] = useState<FeaturePolygon | null>(
    null
  )
  const [annotations, setAnnotations] = useState(staticAnnotations)
  const [mapCenter, setMapCenter] = useState(mainCentroid)

  // Disable zoom and drag when map is locked
  const constrain = (transformMatrix: any, prevTransformMatrix: any) => {
    return locked ? prevTransformMatrix : transformMatrix
  }
  const zoomProps = locked ? { constrain: constrain } : {}

  const closePanel = () => {
    setShowPanel(false)
  }

  const handleFeatureEnter = (feature: FeaturePolygon) => {
    setHoveredFeature(feature)
  }
  const handleFeatureClick = (feature: FeaturePolygon) => {
    setSelectedFeature(feature)
    setShowPanel(true)
  }
  const staticThreshold = scaleThreshold({
    domain: [1500, 2200, 2700, 3600, 4500],
    range: ['#378ebd', '#84aec1', '#d1cec4', '#f1bcab', '#e27875', '#d3343f'],
  })
  const thresholds = {
    ain: {
      appt_price: [1600, 1900, 2100, 2400, 2900, 5800],
      house_price: [1600, 1900, 2200, 2500, 3100, 7900],
    },
    'haute-savoie': {
      appt_price: [3100, 3600, 3900, 4200, 4600, 9700],
      house_price: [3500, 3900, 4100, 4600, 5400, 16900],
    },
  }
  const thresholdScale = useStaticThresholds
    ? staticThreshold
    : scaleThreshold({
        domain:
          thresholds[department][
            propertyType === 'maison' ? 'house_price' : 'appt_price'
          ],
        range: [
          '#378ebd',
          '#84aec1',
          '#d1cec4',
          '#f1bcab',
          '#e27875',
          '#d3343f',
        ],
      })
  const getDataById = (municipality_id: string) => {
    const result = immoData.filter((d) => d.NO_COMMUNE === municipality_id)
    return result.length === 1 ? result[0] : null
  }
  const getPolygonFill = (feature: FeaturePolygon) => {
    if (!feature || !feature.properties) {
      return '#eee'
    }
    const row = getDataById(feature.properties.NO_COMMUNE as unknown as string)
    if (row === null) {
      return '#eee'
    }

    const value_suffix = roomCount === '' ? '' : '_' + roomCount + 'P'
    const cell = ((propertyType === 'appartement'
      ? 'appt_price'
      : 'house_price') + value_suffix) as
      | 'appt_price'
      | 'appt_price_1P'
      | 'appt_price_2P'
      | 'appt_price_3P'
      | 'appt_price_4P'
      | 'house_price'
      | 'house_price_3P'
      | 'house_price_4P'
      | 'house_price_5P'
      | 'house_price_6P'
    const value = row[cell]
    // @ts-ignore
    return value ? thresholdScale(value) : '#eee'
  }
  const getLimitFill = (feature: FeaturePolygon) => {
    const name = feature.properties.name
    return name === 'Genève'
      ? 'var(--gray-1)'
      : name === 'Lac Léman'
      ? 'var(--blue-bright-1)'
      : 'var(--gray-3)'
  }

  // Share municipality names with parent component
  useEffect(() => {
    if (municipalityNames.length > 0) {
      getMunicipalityNames(municipalityNames)
    }
  }, [municipalityNames, getMunicipalityNames])

  // Result for search input
  useEffect(() => {
    if (searchInput === '') {
      setSelectedFeature(null)
      setShowPanel(false)
      setScale(defaultScale)
      setAnnotations(staticAnnotations)
      setShiftY(0)
    } else {
      setScale(zoomedScale)
      setShiftY(-50)
      const result = polygons.filter(
        (d) => d.properties.COMMUNE === searchInput
      )
      if (result.length === 1) {
        panelRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' })
        setSelectedFeature(result[0])
        const firstCoords =
          result[0].geometry.type.toString() === 'Polygon'
            ? result[0].geometry.coordinates[0][0]
            : result[0].geometry.coordinates[0][0][0]
        setMapCenter(firstCoords as [number, number])
        console.log('New center:', result[0].geometry.coordinates[0][0])
        setShowPanel(true)
        let highlightAnnotation:
          | Record<
              string,
              {
                title: string
                subtitle: string
                dx: number
                dy: number
              }
            >
          | {} = {}
        // Add annotation with feature name
        // @ts-ignore
        highlightAnnotation[searchInput] = {
          title: searchInput,
          dx: 50,
          dy: 20,
        }
        setAnnotations({
          ...staticAnnotations,
          ...highlightAnnotation,
        })
      }
    }
  }, [defaultScale, searchInput, zoomedScale, polygons])

  // Load price data
  useEffect(() => {
    fetch(
      'https://storage.googleapis.com/wch-interactive/geodata/real-estate-neighboring-france/immo_data_' +
        dataVersion.file +
        '.json'
    )
      .then((response) => response.json())
      .then((data) => setImmoData(data))
  }, [])

  // Load geodata
  useEffect(() => {
    fetch(
      'https://storage.googleapis.com/wch-interactive/geodata/real-estate-neighboring-france/' +
        department +
        '_simplify_5perc.json'
    )
      .then((response) => response.json())
      .then((data) => {
        const { features: polygons } = topojson.feature(
          data as unknown as Topology,
          data.objects.municipalities as GeometryObject
        ) as unknown as {
          type: 'FeatureCollection'
          features: FeaturePolygon[]
        }
        setPolygons(polygons)
        setMunicipalityNames(
          polygons.map((d) => d.properties.COMMUNE as unknown as string)
        )
        // getMunicipalityNames(
        //   polygons.map((d) => d.properties.COMMUNE as unknown as string)
        // )
      })
  }, [department])

  return (
    <>
      <LegendContainer>
        <LegendTitle>
          Prix moyens en €/m<sup>2</sup>
        </LegendTitle>
        <LegendThreshold
          scale={thresholdScale}
          direction='column-reverse'
          itemDirection='row'
          labelMargin='0 20px 0 0'
          shapeMargin='1px 0 0'
          labelDelimiter='à'
          labelLower='Moins de '
          labelUpper='Plus de '
          labelFormat={(d) =>
            d.toString().length === 5
              ? d.toString().slice(0, 2) + "'" + d.toString().slice(2)
              : d
          }
        />
      </LegendContainer>
      <Container>
        <SVGContainer>
          <ParentSize ignoreDimensions='width'>
            {(parent) =>
              locked ? (
                <svg
                  width={parent.width}
                  height={parent.height}
                  style={{
                    cursor: 'default',
                    touchAction: 'none',
                    background: 'var(--gray-6)',
                    borderRadius: '0.4rem',
                  }}
                >
                  {/* 1. (no rect for pan/zoom interaction) */}
                  {/* 2. Outer cantonal limits and lake */}
                  <Mercator<FeaturePolygon>
                    data={GenevaContour}
                    scale={scale}
                    center={mapCenter}
                    translate={[parent.width / 2, shiftY + parent.height / 2]}
                  >
                    {(mercator) => (
                      <g>
                        {mercator.features.map((item, i) => {
                          const { feature, path } = item
                          const name = feature.properties.name
                          return (
                            <React.Fragment key={`path_${item.index}`}>
                              <path
                                d={path || ''}
                                fill={getLimitFill(feature)}
                                strokeWidth={name === 'Lac Léman' ? 2 : 0}
                                stroke={getLimitFill(feature)}
                                opacity={0.4}
                              />
                            </React.Fragment>
                          )
                        })}
                      </g>
                    )}
                  </Mercator>

                  {/* 3. Municipality Polygons */}
                  <Mercator<FeaturePolygon>
                    data={polygons}
                    scale={scale}
                    center={mapCenter}
                    translate={[parent.width / 2, shiftY + parent.height / 2]}
                  >
                    {(mercator) => (
                      <g className='choropleth'>
                        {mercator.features.map((item, i) => {
                          const { feature, path, centroid } = item

                          return (
                            <path
                              d={path || ''}
                              key={`path_${item.index}`}
                              fill={getPolygonFill(feature)}
                              strokeWidth={0.5}
                              stroke={'#999'}
                              onClick={() => handleFeatureClick(feature)}
                              onMouseEnter={() => {
                                handleFeatureEnter(feature)
                              }}
                              data-centroid={centroid}
                            />
                          )
                        })}
                      </g>
                    )}
                  </Mercator>

                  {/* 4. Main cities (points) */}
                  <Mercator<FeaturePoint>
                    data={cities.filter(
                      (d) =>
                        d.properties.CANT_DEPT !==
                        (department === 'ain' ? 'Haute-Savoie' : 'Ain')
                    )}
                    scale={scale}
                    center={mapCenter}
                    translate={[parent.width / 2, shiftY + parent.height / 2]}
                  >
                    {(mercator) => (
                      <g style={{ pointerEvents: 'none' }}>
                        {mercator.features.map((item, i) => {
                          const { feature, path, centroid } = item
                          const name = feature.properties.COMMUNE
                          return (
                            <React.Fragment key={`dot-${i}`}>
                              <circle
                                cx={centroid[0]}
                                cy={centroid[1]}
                                fill='#000000'
                                r={5}
                                opacity={1}
                              />
                              <text
                                transform={`translate(${centroid})`}
                                fontSize={11}
                                fill='#000000'
                                fontFamily='var(--font-sans-serif)'
                                textAnchor='middle'
                                dx={0}
                                dy={20}
                              >
                                {name.toUpperCase()}
                              </text>
                            </React.Fragment>
                          )
                        })}
                      </g>
                    )}
                  </Mercator>

                  {/* 5. Annotations */}
                  <Mercator<FeaturePolygon>
                    data={polygons}
                    scale={scale}
                    center={mapCenter}
                    translate={[parent.width / 2, shiftY + parent.height / 2]}
                  >
                    {(mercator) => (
                      <g className='text-annotations'>
                        {mercator.features.map((item, i) => {
                          const { feature, path, centroid } = item
                          const name = feature.properties
                            .COMMUNE as unknown as string
                          return (
                            Object.keys(annotations).includes(name) && (
                              <Annotation
                                key={'annotation-' + i.toString()}
                                x={centroid[0]}
                                y={centroid[1]}
                                dx={annotations[name].dx}
                                dy={annotations[name].dy}
                              >
                                <Connector />
                                <CircleSubject />
                                <Label
                                  title={annotations[name].title}
                                  subtitle={annotations[name].subtitle}
                                  width={200}
                                  titleFontSize={14}
                                  subtitleFontSize={12}
                                />
                              </Annotation>
                            )
                          )
                        })}
                      </g>
                    )}
                  </Mercator>
                </svg>
              ) : (
                <Zoom<SVGSVGElement>
                  width={parent.width}
                  height={parent.height}
                  scaleXMin={0.5}
                  scaleXMax={5}
                  scaleYMin={0.5}
                  scaleYMax={5}
                  {...zoomProps}
                >
                  {(zoom) => {
                    const zoomScale = zoom.transformMatrix.scaleX

                    return (
                      <svg
                        width={parent.width}
                        height={parent.height}
                        style={{
                          cursor: locked
                            ? 'default'
                            : zoom.isDragging
                            ? 'grabbing'
                            : 'grab',
                          touchAction: 'none',
                          background: 'var(--gray-6)',
                          borderRadius: '0.4rem',
                        }}
                        ref={zoom.containerRef}
                      >
                        <>
                          {/* 1. Event handler for zoom drag / double click */}

                          <rect
                            className='active'
                            width={parent.width}
                            height={parent.height}
                            rx={14}
                            fill='transparent'
                            onTouchStart={zoom.dragStart}
                            onTouchMove={zoom.dragMove}
                            onTouchEnd={zoom.dragEnd}
                            onMouseDown={zoom.dragStart}
                            onMouseMove={zoom.dragMove}
                            onMouseUp={zoom.dragEnd}
                            onMouseLeave={() => {
                              if (zoom.isDragging) zoom.dragEnd()
                            }}
                            onDoubleClick={(event) => {
                              const point = localPoint(event) || { x: 0, y: 0 }
                              zoom.scale({ scaleX: 1.1, scaleY: 1.1, point })
                            }}
                          />

                          {/* 2. Outer cantonal limits and lake */}
                          <Mercator<FeaturePolygon>
                            data={GenevaContour}
                            scale={scale}
                            center={mapCenter}
                            translate={[
                              parent.width / 2,
                              shiftY + parent.height / 2,
                            ]}
                          >
                            {(mercator) => (
                              <g transform={zoom.toString()}>
                                {mercator.features.map((item, i) => {
                                  const { feature, path } = item
                                  const name = feature.properties.name
                                  return (
                                    <React.Fragment key={`path_${item.index}`}>
                                      <path
                                        d={path || ''}
                                        fill={getLimitFill(feature)}
                                        strokeWidth={
                                          name === 'Lac Léman' ? 2 : 0
                                        }
                                        stroke={getLimitFill(feature)}
                                        opacity={0.4}
                                      />
                                    </React.Fragment>
                                  )
                                })}
                              </g>
                            )}
                          </Mercator>
                          {/* 3. Municipality Polygons */}
                          <Mercator<FeaturePolygon>
                            data={polygons}
                            scale={scale}
                            center={mapCenter}
                            translate={[
                              parent.width / 2,
                              shiftY + parent.height / 2,
                            ]}
                          >
                            {(mercator) => (
                              <g
                                className='choropleth'
                                transform={zoom.toString()}
                              >
                                {mercator.features.map((item, i) => {
                                  const { feature, path, centroid } = item

                                  return (
                                    <path
                                      d={path || ''}
                                      key={`path_${item.index}`}
                                      fill={getPolygonFill(feature)}
                                      strokeWidth={0.5}
                                      stroke={'#999'}
                                      onClick={() =>
                                        handleFeatureClick(feature)
                                      }
                                      data-centroid={centroid}
                                    />
                                  )
                                })}
                              </g>
                            )}
                          </Mercator>

                          {/* 4. Main cities (points) */}
                          <Mercator<FeaturePoint>
                            data={cities.filter(
                              (d) =>
                                d.properties.CANT_DEPT !==
                                (department === 'ain' ? 'Haute-Savoie' : 'Ain')
                            )}
                            scale={scale}
                            center={mapCenter}
                            translate={[
                              parent.width / 2,
                              shiftY + parent.height / 2,
                            ]}
                          >
                            {(mercator) => (
                              <g transform={zoom.toString()}>
                                {mercator.features.map((item, i) => {
                                  const { feature, path, centroid } = item
                                  const name = feature.properties.COMMUNE
                                  return (
                                    <React.Fragment key={`dot-${i}`}>
                                      <circle
                                        cx={centroid[0]}
                                        cy={centroid[1]}
                                        fill='#000000'
                                        r={5}
                                        opacity={1}
                                      />
                                      <text
                                        transform={`translate(${centroid})`}
                                        fontSize={11}
                                        fill='#000000'
                                        fontFamily='var(--font-sans-serif)'
                                        textAnchor='middle'
                                        dx={0}
                                        dy={20}
                                      >
                                        {name.toUpperCase()}
                                      </text>
                                    </React.Fragment>
                                  )
                                })}
                              </g>
                            )}
                          </Mercator>

                          {/* 5. Annotations */}
                          <Mercator<FeaturePolygon>
                            data={polygons}
                            scale={scale}
                            center={mapCenter}
                            translate={[
                              parent.width / 2,
                              shiftY + parent.height / 2,
                            ]}
                          >
                            {(mercator) => (
                              <g
                                transform={zoom.toString()}
                                className='text-annotations'
                              >
                                {mercator.features.map((item, i) => {
                                  const { feature, path, centroid } = item
                                  const name = feature.properties
                                    .COMMUNE as unknown as string
                                  return (
                                    Object.keys(annotations).includes(name) && (
                                      <Annotation
                                        key={'annotation-' + i.toString()}
                                        x={centroid[0]}
                                        y={centroid[1]}
                                        dx={annotations[name].dx}
                                        dy={annotations[name].dy}
                                      >
                                        <Connector />
                                        <CircleSubject />
                                        <Label
                                          title={annotations[name].title}
                                          subtitle={annotations[name].subtitle}
                                          width={200}
                                          titleFontSize={14 / zoomScale}
                                          subtitleFontSize={12 / zoomScale}
                                        />
                                      </Annotation>
                                    )
                                  )
                                })}
                              </g>
                            )}
                          </Mercator>
                        </>
                      </svg>
                    )
                  }}
                </Zoom>
              )
            }
          </ParentSize>
        </SVGContainer>

        <Tooltip></Tooltip>
        <PanelContainer
          ref={panelRef}
          style={{ height: showPanel ? height / 2 : 0 }}
        >
          {showPanel && (
            <Panel
              feature={selectedFeature}
              data={getDataById(selectedFeature?.properties?.NO_COMMUNE!)}
              showPanel={showPanel}
              dataSelection={propertyType}
              dataVersion={dataVersion}
              handleClose={closePanel}
            />
          )}
        </PanelContainer>
      </Container>
    </>
  )
}

const Container = styled.div`
  width: 100%;
  height: ${height}px;
  .choropleth {
    path {
      transition: fill 0.5s ease-in-out;
      cursor: pointer;
    }
  }
  display: grid;
  grid-template-rows: repeat(2, 1fr);
`

// SVG container
const SVGContainer = styled.div`
  width: 100%;
  height: ${height}px;
  grid-row-start: 1;
  grid-column-start: 1;
`

const Tooltip = styled.div`
  position: absolute;
`

const PanelContainer = styled.div`
  opacity: 0.8;
  margin-top: -${height / 2}px;
  grid-row-start: 2;
`

const LegendContainer = styled.div`
  @media (min-width: 600px) {
    position: absolute;
    border: 1px solid #ccc;
    border-width: 0 1px 1px 0;
    background-color: var(--site-background);
    border-radius: 0 0 1em 0;
  }
  padding: 1em;
  font-size: 1.4rem;
  line-height: 2rem;
  .visx-legend-shape,
  svg {
    padding-right: 0.5rem;
  }
`

const LegendTitle = styled.h4`
  margin-bottom: 0.5rem;
  line-height: 1.2;
`

export default Map
